// Место загрузки ролика для `VideoUpload`: регистрирует его в хранилище фоновых
// загрузок, читает оттуда прогресс и ставит новую загрузку. Прогресс берётся из
// хранилища по МЕСТУ, а не из состояния компонента, — вернувшись в блок, автор
// видит идущую загрузку, а не пустую зону.
import { useEffect, useId, useRef, useSyncExternalStore } from 'react'
import { useBlockDraftGate } from '../studio/BlockDraftContext'
import { placeKey, type ApplyImageToBlock, type BlockUploadTarget } from './uploadTask'
import { useVideoUploads } from './VideoUploadContext'
import type { VideoSlot, VideoUploadTarget } from './videoUploadStore'

/** Kinescope id — в слот языка видео-блока. Чистая функция: в фоне она
 *  применяется к СВЕЖЕМУ серверному блоку (`PUT` блока — полная замена тела). */
export const applyVideoToBlock =
  (slot: VideoSlot): ApplyImageToBlock =>
  (block, videoId) =>
    block.type === 'video' ? { ...block, [slot]: videoId } : null

export const useVideoUploadPlace = (
  slot: VideoSlot | undefined,
  onUploaded: (videoId: string) => void,
) => {
  const store = useVideoUploads()
  const instanceKey = useId()
  // Владельца называет точка вызова (узкая проекция черновика открытого блока),
  // а не глобальное выделение — оно к концу загрузки уже уедет.
  const { blockId, lessonId } = useBlockDraftGate()
  const block: BlockUploadTarget | undefined =
    slot && blockId !== null && lessonId !== null
      ? { blockId, lessonId, placeId: slot, apply: applyVideoToBlock(slot) }
      : undefined
  const target: VideoUploadTarget = { key: block ? placeKey(block) : instanceKey, block }

  const onUploadedRef = useRef(onUploaded)
  const targetRef = useRef(target)
  useEffect(() => {
    onUploadedRef.current = onUploaded
    targetRef.current = target
  })
  // Место «на экране», пока компонент обслуживает ИМЕННО его: соседний блок
  // того же типа реконсилирует тот же экземпляр, поэтому перерегистрация — по ключу.
  useEffect(
    () => store.attach(targetRef.current, (id) => onUploadedRef.current(id)),
    [store, target.key],
  )

  const progress = useSyncExternalStore(store.subscribe, () => store.progressOf(target))
  const fromCanvas = useSyncExternalStore(store.subscribe, () => store.fromCanvasOf(target))
  const start = (file: File, title: string, canvas = false) =>
    void store.start(target, file, title, canvas)
  return { progress, fromCanvas, start }
}
